import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, Loader2, ExternalLink, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { chat } from '@/lib/api';

// Smart routing keywords and responses
const getSmartResponses = (t) => ({
  // Kargo / Shipping
  kargo: {
    keywords: ['kargo', 'cargo', 'shipping', 'teslimat', 'delivery', 'gönderim', 'takip', 'track', 'tracking', 'paket', 'package', 'kargom', 'kargom nerede', 'sipariş takip'],
    response: t('chat.responses.shipping', 'Kargo takibi için sipariş numaranızı kullanarak aşağıdaki sayfadan kargo durumunuzu öğrenebilirsiniz.'),
    action: { type: 'link', url: '/shipping', label: t('chat.buttons.trackOrder', 'Kargo Takip Sayfası') }
  },
  
  // Sipariş / Order
  siparis: {
    keywords: ['sipariş', 'siparişim', 'order', 'orders', 'satın aldım', 'bought', 'purchase', 'sipariş verdim', 'siparişlerim'],
    response: t('chat.responses.order', 'Siparişlerinizi görüntülemek için hesabınıza giriş yapmanız gerekmektedir. Aşağıdaki butonla siparişlerinize ulaşabilirsiniz.'),
    action: { type: 'link', url: '/dashboard/orders', label: t('chat.buttons.myOrders', 'Siparişlerim') }
  },
  
  // Ürün / Product - Online satış ürünleri
  urun: {
    keywords: ['ürün', 'product', 'ürünler', 'products', 'ne satıyorsunuz', 'what do you sell', 'katalog', 'catalog', 'liste', 'list', 'online', 'satın al', 'buy'],
    response: t('chat.responses.products', 'Online mağazamızda Türk ürünleri, ev tekstili, gıda ürünleri ve daha fazlasını bulabilirsiniz. Hemen alışverişe başlayın!'),
    action: { type: 'link', url: '/products', label: t('chat.buttons.browseProducts', 'Online Mağazaya Git') }
  },
  
  // Restoran
  restoran: {
    keywords: ['restoran', 'restaurant', 'yemek', 'food', 'lokanta', 'kebap', 'kebab', 'türk mutfağı', 'turkish food', 'restoranlar', 'türk restoranları', 'restoranları görmek', 'restoranlarını görmek'],
    response: t('chat.responses.restaurants', 'Dünyanın dört bir yanındaki Türk restoranlarını keşfedin! Size en yakın Türk restoranını bulabilir ve menülerini inceleyebilirsiniz.'),
    action: { type: 'link', url: '/restaurants', label: t('chat.buttons.findRestaurants', 'Türk Restoranlarını Gör') }
  },
  
  // Restoran Ekleme / Restaurant Registration
  restoranEkle: {
    keywords: ['restoran ekle', 'restoran nasıl', 'restaurant add', 'restoranımı ekle', 'restoran kayıt', 'restoran aktif', 'activate restaurant', 'restoran sahibi', 'restaurant owner'],
    response: t('chat.responses.addRestaurant', 'Restoranınızı platformumuza eklemek veya mevcut kaydınızı aktifleştirmek için satıcı kayıt sayfasından başvuru yapabilirsiniz.'),
    action: { type: 'link', url: '/seller-register?category=restaurants', label: t('chat.buttons.addRestaurant', 'Restoran Ekle / Aktifleştir') }
  },
  
  // Satıcı Olma / Become Seller
  satici: {
    keywords: ['satıcı', 'seller', 'satış yap', 'sell', 'mağaza aç', 'open store', 'nasıl satarım', 'how to sell', 'satıcı ol', 'become seller', 'ürün sat', 'partner'],
    response: t('chat.responses.becomeSeller', 'Türk ürünlerinizi dünyaya satmak ister misiniz? Satıcı olarak aramıza katılın! Ücretsiz kayıt olabilirsiniz.'),
    action: { type: 'link', url: '/seller-register', label: t('chat.buttons.becomeSeller', 'Satıcı Ol') }
  },
  
  // Ülkeler / Countries
  ulkeler: {
    keywords: ['ülke', 'country', 'countries', 'nerede', 'where', 'hangi ülkeler', 'which countries', 'faaliyet', 'operate', 'hizmet', 'service', 'bölge', 'region'],
    response: t('chat.responses.countries', 'Türkiye\'nin 7 bölgesinden 7 kıtaya hizmet veriyoruz! Almanya, Fransa, İngiltere, ABD, Kanada ve daha birçok ülkede aktifiz. Detaylı bilgi için iletişim sayfamızı ziyaret edin.'),
    action: { type: 'link', url: '/contact', label: t('chat.buttons.contactUs', 'İletişim') }
  },
  
  // Hakkımızda / About Us
  hakkimizda: {
    keywords: ['hakkında', 'about', 'kimsiniz', 'who are you', 'ne zaman', 'when', 'kaç yıl', 'how many years', 'tarihçe', 'history', 'kuruluş', 'founded', 'hikaye', 'story'],
    response: t('chat.responses.aboutUs', 'Turkish E-Market, Türk ürünlerini dünyaya tanıtan ilk ve tek global Türk e-ticaret platformudur. Hikayemizi öğrenmek için Hakkımızda sayfasını ziyaret edin.'),
    action: { type: 'link', url: '/about', label: t('chat.buttons.aboutUs', 'Hakkımızda') }
  },
  
  // İletişim / Contact
  iletisim: {
    keywords: ['iletişim', 'contact', 'telefon', 'phone', 'email', 'e-posta', 'adres', 'address', 'ulaş', 'reach', 'destek', 'support'],
    response: t('chat.responses.contact', 'Bizimle iletişime geçmek için aşağıdaki butonu kullanabilirsiniz. Size en kısa sürede dönüş yapacağız.'),
    action: { type: 'link', url: '/contact', label: t('chat.buttons.contactPage', 'İletişim Sayfası') }
  },
  
  // İade / Return
  iade: {
    keywords: ['iade', 'return', 'geri gönder', 'send back', 'değişim', 'exchange', 'para iade', 'refund', 'iptal', 'cancel'],
    response: t('chat.responses.returns', '14 gün içinde koşulsuz iade hakkınız bulunmaktadır. İade işlemleri için siparişleriniz sayfasından talep oluşturabilirsiniz.'),
    action: { type: 'link', url: '/dashboard/orders', label: t('chat.buttons.returnRequest', 'İade Talebi') }
  },
  
  // Ödeme / Payment
  odeme: {
    keywords: ['ödeme', 'payment', 'kredi kartı', 'credit card', 'banka', 'bank', 'havale', 'transfer', 'nasıl öderim', 'how to pay'],
    response: t('chat.responses.payment', 'Kredi kartı, banka kartı ve havale/EFT ile ödeme yapabilirsiniz. Tüm ödemeleriniz 256-bit SSL ile güvence altındadır.'),
    action: null
  },
  
  // Süpermarket / Türk Marketleri
  supermarket: {
    keywords: ['süpermarket', 'supermarket', 'market', 'bakkal', 'grocery', 'gıda', 'yiyecek', 'türk marketi', 'turkish market', 'marketler', 'türk marketleri', 'marketleri görmek'],
    response: t('chat.responses.supermarket', 'Dünyanın dört bir yanındaki Türk marketlerini keşfedin! Türk gıda ürünleri, bakliyat, baharat ve daha fazlası için size en yakın Türk marketini bulun.'),
    action: { type: 'link', url: '/supermarkets', label: t('chat.buttons.findSupermarket', 'Türk Marketlerini Gör') }
  },
  
  // Araç Kiralama / Car Rental
  arac: {
    keywords: ['araç', 'araba', 'car', 'kiralama', 'rental', 'rent', 'oto', 'auto', 'araç kirala'],
    response: t('chat.responses.carRental', 'Türkiye\'de ve yurt dışında araç kiralama hizmeti sunuyoruz. En uygun fiyatlarla araç kiralayın.'),
    action: { type: 'link', url: '/cars', label: t('chat.buttons.rentCar', 'Araç Kirala') }
  },
  
  // Otel / Hotel
  otel: {
    keywords: ['otel', 'hotel', 'konaklama', 'accommodation', 'rezervasyon', 'reservation', 'oda', 'room', 'kalacak yer'],
    response: t('chat.responses.hotels', 'Türkiye\'nin en güzel otellerinde konaklayın. Online rezervasyon yapabilirsiniz.'),
    action: { type: 'link', url: '/hotels', label: t('chat.buttons.bookHotel', 'Otel Rezervasyonu') }
  },
  
  // Merhaba / Greeting
  merhaba: {
    keywords: ['merhaba', 'hello', 'hi', 'selam', 'hey', 'günaydın', 'good morning', 'iyi günler', 'good day'],
    response: t('chat.responses.greeting', 'Merhaba! 👋 Turkish E-Market\'e hoş geldiniz. Size nasıl yardımcı olabilirim?'),
    action: null
  },
  
  // Teşekkür / Thanks
  tesekkur: {
    keywords: ['teşekkür', 'thank', 'thanks', 'sağol', 'eyvallah', 'mersi'],
    response: t('chat.responses.thanks', 'Rica ederim! Başka bir konuda yardımcı olabilir miyim? 😊'),
    action: null
  }
});

// Quick action suggestions - both label and message should be translated
const getQuickActions = (t) => [
  { 
    label: t('chat.quick.trackOrder', 'Kargom nerede?'), 
    message: t('chat.quick.trackOrderMsg', 'Kargom nerede?'),
    intent: 'kargo'
  },
  { 
    label: t('chat.quick.turkishMarkets', 'Türk Marketleri'), 
    message: t('chat.quick.turkishMarketsMsg', 'Türk marketlerini görmek istiyorum'),
    intent: 'supermarket'
  },
  { 
    label: t('chat.quick.restaurants', 'Türk Restoranları'), 
    message: t('chat.quick.restaurantsMsg', 'Türk restoranlarını görmek istiyorum'),
    intent: 'restoran'
  },
  { 
    label: t('chat.quick.becomeSeller', 'Satıcı olmak istiyorum'), 
    message: t('chat.quick.becomeSellerMsg', 'Nasıl satıcı olurum?'),
    intent: 'satici'
  }
];

export const ChatWidget = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  /** Widget için kalıcı oturum (Mongo chat_messages.session_id ile eşleşir) */
  const widgetSessionRef = useRef(null);
  const scrollRef = useRef(null);
  const [langKey, setLangKey] = useState(0);
  const langInitRef = useRef(true);

  const currentLanguage = i18n.language;

  const ensureWidgetSid = React.useCallback(() => {
    if (widgetSessionRef.current) return widgetSessionRef.current;
    try {
      let s =
        typeof localStorage !== 'undefined' ? localStorage.getItem('tm_chat_widget_sid') : '';
      if (!s || s.length < 8) {
        s = crypto.randomUUID();
        localStorage.setItem('tm_chat_widget_sid', s);
      }
      widgetSessionRef.current = s;
      return s;
    } catch {
      const fb = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      widgetSessionRef.current = fb;
      return fb;
    }
  }, []);

  const persistSmartExchange = React.useCallback(
    async (userMsg, assistantMsg) => {
      try {
        const sid = ensureWidgetSid();
        const langCode = String(i18n.language || 'en').split('-')[0].toLowerCase();
        await chat.persistExchange({
          session_id: sid,
          language: langCode,
          user_message: userMsg,
          assistant_message: assistantMsg || '',
        });
      } catch (err) {
        console.warn('[ChatWidget] persistExchange failed:', err?.message || err);
      }
    },
    [ensureWidgetSid, i18n.language],
  );

  // Recalculate smart responses and quick actions based on current language
  const smartResponses = React.useMemo(() => getSmartResponses(t), [currentLanguage, t]);
  const quickActions = React.useMemo(() => getQuickActions(t), [currentLanguage, t]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!isOpen) return;
    const pullAdminReplies = async () => {
      const sid = widgetSessionRef.current;
      if (!sid) return;
      try {
        const { data } = await chat.history(sid);
        const rows = Array.isArray(data) ? data : data?.messages || [];
        const incoming = rows.filter((m) => m?.role === 'admin' && (m.content || '').trim());
        if (!incoming.length) return;
        setMessages((prev) => {
          const seen = new Set(
            prev.filter((m) => m.role === 'admin').map((m) => (m.content || '').trim()),
          );
          const extra = incoming
            .filter((m) => !seen.has((m.content || '').trim()))
            .map((m) => ({ role: 'admin', content: m.content }));
          return extra.length ? [...prev, ...extra] : prev;
        });
      } catch {
        /* ignore */
      }
    };
    pullAdminReplies();
    const t = setInterval(pullAdminReplies, 5000);
    return () => clearInterval(t);
  }, [isOpen]);

  // Dil değişince yeni görüşme oturumu (admin tarafında ayrışır)
  useEffect(() => {
    if (langInitRef.current) {
      langInitRef.current = false;
      ensureWidgetSid();
      return;
    }
    try {
      const s = crypto.randomUUID();
      localStorage.setItem('tm_chat_widget_sid', s);
      widgetSessionRef.current = s;
    } catch {
      widgetSessionRef.current = crypto.randomUUID();
    }
    setMessages([]);
    setLangKey((prev) => prev + 1);
  }, [currentLanguage, ensureWidgetSid]);

  // Find matching smart response
  const findSmartResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    
    // Priority order for keyword matching - more specific categories first
    const priorityOrder = [
      'kargo',        // "kargom nerede" must match first
      'restoranEkle',  // "restoran ekle" before "restoran"
      'restoran',
      'supermarket',
      'satici',
      'siparis',
      'iade',
      'odeme',
      'urun',
      'arac',
      'otel',
      'ulkeler',
      'hakkimizda',
      'iletisim',
      'merhaba',
      'tesekkur'
    ];
    
    // Check categories in priority order
    for (const key of priorityOrder) {
      const data = smartResponses[key];
      if (!data) continue;
      
      for (const keyword of data.keywords) {
        if (lowerMessage.includes(keyword.toLowerCase())) {
          return data;
        }
      }
    }
    return null;
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const smartResponse = findSmartResponse(userMessage);
      
      if (smartResponse) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: smartResponse.response,
          action: smartResponse.action
        }]);
        await persistSmartExchange(userMessage, smartResponse.response);
      } else {
        const sid = ensureWidgetSid();
        const langCode = String(i18n.language || 'en').split('-')[0].toLowerCase();
        const response = await chat.send({
          message: userMessage,
          session_id: sid,
          language: langCode,
        });
        const newSid = response.data.session_id;
        if (newSid && newSid !== sid) {
          widgetSessionRef.current = newSid;
          try {
            localStorage.setItem('tm_chat_widget_sid', newSid);
          } catch {/* ignore */}
        }
        setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: t('chat.error', 'Üzgünüm, bir hata oluştu. Lütfen daha sonra tekrar deneyin.')
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    // If action has intent, use it directly for smart response matching
    if (action.intent && smartResponses[action.intent]) {
      const response = smartResponses[action.intent];
      setMessages([
        ...messages,
        { role: 'user', content: action.message },
        { role: 'assistant', content: response.response, action: response.action }
      ]);
      void persistSmartExchange(action.message, response.response);
    } else {
      // Fallback to message-based handling
      setInput(action.message);
      setTimeout(() => {
        const fakeEvent = { preventDefault: () => {} };
        setInput(action.message);
        handleSend(fakeEvent);
      }, 100);
    }
  };

  const handleActionClick = (action) => {
    if (action.type === 'link') {
      navigate(action.url);
      setIsOpen(false);
    }
  };

  return (
    <div className="fixed right-6 z-50 bottom-[5.5rem] sm:bottom-6" data-testid="chat-widget">
      {/* Chat window */}
      {isOpen && (
        <div key={`chat-window-${langKey}`} className="absolute bottom-16 right-0 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border overflow-hidden animate-slide-up">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-4 flex items-center justify-between">
            <div>
              <h3 className="font-medium">{t('chat.title', 'Müşteri Desteği')}</h3>
              <p className="text-xs text-white/80">{t('chat.subtitle', 'Size nasıl yardımcı olabiliriz?')}</p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20"
              data-testid="chat-close-btn"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Messages */}
          <ScrollArea className="h-80 p-4" ref={scrollRef}>
            {messages.length === 0 && (
              <div key={`quick-actions-${langKey}`} className="text-center text-muted-foreground py-4">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm mb-4">{t('chat.welcome', 'Merhaba! Size nasıl yardımcı olabilirim?')}</p>
                <div className="space-y-2">
                  {quickActions.map((action, idx) => (
                    <button
                      key={`${langKey}-${idx}`}
                      onClick={() => handleQuickAction(action)}
                      className="block w-full text-left text-sm px-3 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors flex items-center gap-2"
                    >
                      <ChevronRight className="w-4 h-4 text-red-500" />
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className="mb-3">
                <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
                      msg.role === 'user'
                        ? 'bg-red-600 text-white rounded-br-none'
                        : msg.role === 'admin'
                        ? 'bg-blue-600 text-white rounded-bl-none'
                        : 'bg-gray-100 text-foreground rounded-bl-none'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
                {/* Action button */}
                {msg.action && (
                  <div className="mt-2 ml-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleActionClick(msg.action)}
                      className="text-red-600 border-red-200 hover:bg-red-50 flex items-center gap-2"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {msg.action.label}
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start mb-3">
                <div className="bg-gray-100 rounded-2xl rounded-bl-none px-4 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                </div>
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <form onSubmit={handleSend} className="p-4 border-t bg-gray-50">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('chat.placeholder', 'Mesajınızı yazın...')}
                className="flex-1 rounded-full bg-white"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                size="icon" 
                className="rounded-full bg-red-600 hover:bg-red-700" 
                disabled={loading || !input.trim()}
                data-testid="chat-send-btn"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
        data-testid="chat-toggle-btn"
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </div>
  );
};
